import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, newIdempotencyKey } from '../../lib/api'
import { Permissions } from '../../auth/permissions'
import type { EtimsDetail, EtimsStatus } from '../messaging/api'

/**
 * Customer support and credit notes (milestone 17): the search that finds a sale by what
 * the customer knows, tickets, warranty cases, and credit notes with their approval.
 */

export type TicketType = 'WarrantyClaim' | 'Replacement' | 'Return' | 'Exchange' | 'BillingDispute' | 'DeliveryComplaint'
export type TicketStatus = 'New' | 'Assigned' | 'InProgress' | 'WaitingOnCustomer' | 'Resolved' | 'Closed'
export type TicketPriority = 'Low' | 'Normal' | 'High' | 'Urgent'
export type EscalationLevel = 'Shop' | 'Regional' | 'Finance'
export type TicketEventKind = 'Created' | 'Assigned' | 'StatusChanged' | 'Note' | 'Escalated' | 'Document' | 'Warranty'
export type WarrantyCaseStatus = 'Open' | 'AtRepairCentre' | 'Repaired' | 'Replaced' | 'Refused'
export type CreditNoteStatus = 'PendingApproval' | 'Posted' | 'Rejected'
export type RefundMethod = 'ToAccount' | 'Cash' | 'Mpesa' | 'Bank' | 'ExchangeCredit'

export const ticketTypeLabels: Record<TicketType, string> = {
  WarrantyClaim: 'Warranty claim (repair)',
  Replacement: 'Replacement',
  Return: 'Return for a refund',
  Exchange: 'Exchange',
  BillingDispute: 'Billing dispute',
  DeliveryComplaint: 'Delivery complaint',
}

export const ticketStatusLabels: Record<TicketStatus, string> = {
  New: 'New', Assigned: 'Assigned', InProgress: 'In progress', WaitingOnCustomer: 'Waiting on customer', Resolved: 'Resolved', Closed: 'Closed',
}

export const levelLabels: Record<EscalationLevel, string> = { Shop: 'Shop manager', Regional: 'Regional manager', Finance: 'Finance' }

export const warrantyStatusLabels: Record<WarrantyCaseStatus, string> = {
  Open: 'Open', AtRepairCentre: 'At the repair centre', Repaired: 'Repaired', Replaced: 'Replaced', Refused: 'Refused',
}

export const refundLabels: Record<RefundMethod, string> = {
  ToAccount: 'Credit their account', Cash: 'Cash from my till', Mpesa: 'Sent by M-Pesa', Bank: 'Bank transfer', ExchangeCredit: 'Exchange credit',
}

export const creditStatusLabels: Record<CreditNoteStatus, string> = { PendingApproval: 'Awaiting approval', Posted: 'Approved', Rejected: 'Rejected' }

/** Who may see the credit notes: the register, those who raise or approve them, and the support desk. */
export const creditNoteViewers = [Permissions.Sales.ViewRegister, Permissions.Sales.CreateCreditNote, Permissions.Sales.ApproveCreditNote, Permissions.Support.View]

// The lists and pages below read with staleTime 0: they change by other people's hands —
// an approval, an escalation, a credit spent at another till — and a desk shared by
// several people must show that on the next visit, not thirty seconds later.

export const supportKeys = {
  all: ['support'] as const,
  lookup: (q: string) => ['support', 'lookup', q] as const,
  tickets: (filters: object) => ['support', 'tickets', filters] as const,
  ticket: (id: string) => ['support', 'ticket', id] as const,
  assignees: (locationId: string) => ['support', 'assignees', locationId] as const,
  creditNotes: (filters: object) => ['support', 'credit-notes', filters] as const,
  creditNote: (id: string) => ['support', 'credit-note', id] as const,
  creditable: (invoiceId: string) => ['support', 'creditable', invoiceId] as const,
}

// --- the search -------------------------------------------------------------------------------

export type LookupCustomer = { id: string; code: string; name: string; phone?: string; isWalkIn: boolean }
export type LookupLine = {
  id: string; productId: string; productCode: string; productName: string; quantity: number; lineTotal: number
  hasWarranty: boolean; coveredUntil?: string; covered: boolean
}
export type LookupInvoice = {
  id: string; code: string; orderNumber?: string; invoiceDate: string; customerId: string; customerName: string; customerPhone?: string
  locationId: string; locationName: string; grandTotal: number; isReversed: boolean; lines: LookupLine[]
}
export type SupportLookup = { customers: LookupCustomer[]; invoices: LookupInvoice[] }

export function useSupportLookup(q: string) {
  const term = q.trim()
  return useQuery({
    queryKey: supportKeys.lookup(term),
    queryFn: () => api<SupportLookup>(`/api/support/lookup?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 3,
    placeholderData: keepPreviousData,
  })
}

// --- tickets ------------------------------------------------------------------------------------

export type TicketListItem = {
  id: string; code: string; type: TicketType; status: TicketStatus; priority: TicketPriority; subject: string
  customerId: string; customerName: string; contactPhone?: string; locationId: string; locationName: string
  assignedToUserId?: string; assignedToName?: string; escalationLevel: EscalationLevel; openedAt: string; escalatesAt?: string; invoiceCode?: string
}
export type TicketPage = {
  items: TicketListItem[]; total: number; page: number; pageSize: number
  counts: { open: number; mine: number; unassigned: number; disputes: number }
}
export type TicketEventItem = { kind: TicketEventKind; text: string; by?: string; at: string }
export type WarrantyCaseItem = {
  id: string; code: string; status: WarrantyCaseStatus; soldOn: string; coveredUntil: string; repairCentre?: string
  sentAt?: string; returnedAt?: string; supplierName?: string; supplierClaimReference?: string; outcome?: string
  replacedQuantity?: number; replacementCost?: number; soldQuantity: number; terms?: string; replacementPolicy?: string
}
export type TicketCreditNote = { id: string; code: string; status: CreditNoteStatus; total: number; refundMethod: RefundMethod }
export type TicketDetail = {
  ticket: TicketListItem; description: string; resolution?: string; resolvedAt?: string; closedAt?: string
  invoiceId?: string; invoiceLineId?: string; productId?: string; productCode?: string; productName?: string; customerPhone?: string
  needsDocument: boolean; warrantyNote?: string; warrantyCase?: WarrantyCaseItem; creditNote?: TicketCreditNote; events: TicketEventItem[]
}
export type TicketFilters = { status?: string; type?: TicketType; mine?: boolean; unassigned?: boolean; search?: string; locationId?: string; page: number }

export function useTickets(filters: TicketFilters) {
  return useQuery({
    staleTime: 0,
    queryKey: supportKeys.tickets(filters),
    queryFn: () => api<TicketPage>(`/api/support/tickets?${query(filters)}`),
    placeholderData: keepPreviousData,
  })
}

export function useTicket(id: string | undefined) {
  return useQuery({
    staleTime: 0,
    queryKey: supportKeys.ticket(id ?? ''),
    queryFn: () => api<TicketDetail>(`/api/support/tickets/${id}`),
    enabled: !!id,
  })
}

export function useAssignees(locationId: string | undefined) {
  return useQuery({
    queryKey: supportKeys.assignees(locationId ?? ''),
    queryFn: () => api<{ id: string; name: string; role: string }[]>(`/api/support/assignees?locationId=${locationId}`),
    enabled: !!locationId,
  })
}

export type TicketDraft = {
  type: TicketType; priority: TicketPriority; subject: string; description?: string; customerId: string; contactPhone?: string
  invoiceId?: string; invoiceLineId?: string; productId?: string; locationId?: string
}
export type TicketOpened = { id: string; code: string; warrantyCaseId?: string; warrantyNote?: string; assignedToUserId?: string }

export function useOpenTicket() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ draft, idempotencyKey }: { draft: TicketDraft; idempotencyKey: string }) =>
      api<TicketOpened>('/api/support/tickets', { method: 'POST', body: draft, idempotencyKey }),
    onSuccess: () => client.invalidateQueries({ queryKey: supportKeys.all }),
  })
}

/** Every change to a ticket, then the ticket and the lists read again. */
export function useTicketAction(ticketId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api<unknown>(path.startsWith('/') ? path : `/api/support/tickets/${ticketId}/${path}`, { method: 'POST', body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => client.invalidateQueries({ queryKey: supportKeys.all }),
  })
}

// --- credit notes -------------------------------------------------------------------------------

export type CreditNoteListItem = {
  id: string; code: string; status: CreditNoteStatus; invoiceId: string; invoiceCode: string; customerId: string; customerName: string
  locationId: string; locationName: string; refundMethod: RefundMethod; total: number; reason: string
  requestedBy?: string; requestedAt: string; decidedBy?: string; decidedAt?: string; etimsStatus?: EtimsStatus
  exchangeCreditRemaining: number; ticketCode?: string
}
export type CreditNotePage = {
  items: CreditNoteListItem[]; total: number; page: number; pageSize: number
  counts: { pending: number; posted: number; rejected: number }
}
export type CreditNoteLineItem = {
  lineNumber: number; productCode: string; productName: string; quantity: number; netAmount: number; vatRate: number
  vatAmount: number; total: number; isReturn: boolean
}
export type CreditNoteDetail = {
  note: CreditNoteListItem; refundReference?: string; rejectionReason?: string; ticketId?: string; netTotal: number; vatTotal: number
  costReversed?: number; redeemedAmount: number; canDecide: boolean; whyNotDecide?: string; lines: CreditNoteLineItem[]; etims?: EtimsDetail
}
export type CreditableLine = {
  id: string; lineNumber: number; productId: string; productCode: string; productName: string; quantity: number; unitPrice: number
  lineNet: number; lineTotal: number; vatRate: number; creditedQuantity: number; creditedNet: number; remainingQuantity: number; remainingNet: number
}
export type CreditableInvoice = {
  id: string; code: string; invoiceDate: string; customerId: string; customerName: string; customerIsWalkIn: boolean
  locationId: string; locationName: string; grandTotal: number; hasOpenTill: boolean; lines: CreditableLine[]; creditNotes: CreditNoteListItem[]
}
export type CreditNoteFilters = { status?: CreditNoteStatus; search?: string; locationId?: string; page: number }

export function useCreditNotes(filters: CreditNoteFilters) {
  return useQuery({
    staleTime: 0,
    queryKey: supportKeys.creditNotes(filters),
    queryFn: () => api<CreditNotePage>(`/api/sales/credit-notes?${query(filters)}`),
    placeholderData: keepPreviousData,
  })
}

/** Polls while its eTIMS report is on its way. */
export function useCreditNote(id: string | undefined) {
  return useQuery({
    staleTime: 0,
    queryKey: supportKeys.creditNote(id ?? ''),
    queryFn: () => api<CreditNoteDetail>(`/api/sales/credit-notes/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.etims?.status === 'Pending' ? 2000 : false),
  })
}

export function useCreditable(invoiceId: string | undefined) {
  return useQuery({
    queryKey: supportKeys.creditable(invoiceId ?? ''),
    queryFn: () => api<CreditableInvoice>(`/api/sales/credit-notes/for-invoice/${invoiceId}`),
    enabled: !!invoiceId,
  })
}

export type CreditNoteDraft = {
  invoiceId: string
  lines: { invoiceLineId: string; quantity: number; amount?: number }[]
  refund: RefundMethod
  refundReference?: string
  reason: string
  ticketId?: string
}

export function useRaiseCreditNote() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ draft, idempotencyKey }: { draft: CreditNoteDraft; idempotencyKey: string }) =>
      api<{ id: string; code: string; total: number; status: CreditNoteStatus }>('/api/sales/credit-notes', { method: 'POST', body: draft, idempotencyKey }),
    onSuccess: () => client.invalidateQueries({ queryKey: supportKeys.all }),
  })
}

export function useDecideCreditNote(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (decision: { approve: true } | { approve: false; reason: string }) =>
      decision.approve
        ? api<void>(`/api/sales/credit-notes/${id}/approve`, { method: 'POST', idempotencyKey: newIdempotencyKey() })
        : api<void>(`/api/sales/credit-notes/${id}/reject`, { method: 'POST', body: { reason: decision.reason } }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: supportKeys.all })
      client.invalidateQueries({ queryKey: ['messaging'] })
    },
  })
}

export function useRetryCreditNoteEtims(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api<void>(`/api/sales/credit-notes/${id}/etims/retry`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: supportKeys.creditNote(id) }),
  })
}

/** An exchange credit, found at the till by its credit note number. */
export type ExchangeCredit = { id: string; code: string; customerId: string; customerName: string; forWalkIn: boolean; remaining: number; invoiceCode: string }

export function findExchangeCredit(code: string): Promise<ExchangeCredit> {
  return api<ExchangeCredit>(`/api/pos/exchange-credits/${encodeURIComponent(code.trim().toUpperCase())}`)
}

function query(filters: object): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '' && value !== false) params.set(key, String(value))
  }
  return params.toString()
}
